/**
 * useVideoProgress — AC5/6: timeupdate listener, 15s debounce, auto-complete at 90%.
 * Fire-and-forget: never blocks playback.
 */
import { useRef, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8001';

export function useVideoProgress(
  videoRef: React.RefObject<HTMLVideoElement>,
  videoId: string,
  durationSec: number,
) {
  const lastReported = useRef(0);

  useEffect(() => {
    lastReported.current = 0; // H2: reset on videoId change — avoids tracking gap when navigating between videos
    const video = videoRef.current;
    if (!video || !videoId) return;

    const handleTimeUpdate = () => {
      const current = Math.floor(video.currentTime);
      if (current - lastReported.current < 15) return;
      lastReported.current = current;
      const completed = durationSec > 0 && current / durationSec >= 0.9;
      const token = localStorage.getItem('token') ?? '';
      // fire-and-forget — never await, never block playback
      fetch(`${API_BASE}/api/v1/knowledge/videos/${videoId}/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ watched_seconds: current, completed }),
      }).catch(() => {});
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [videoId, durationSec]); // L1: videoRef is a stable ref — omit from deps
}
